/**
 * Audit Logs Listing Page
 * 
 * Main page for viewing audit logs with:
 * - Table listing all audit logs
 * - Filters by promo, action, date range
 * - Search functionality
 * - Pagination
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Card, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

// Common components
import Breadcrumbs from '../../../Common/Breadcrumbs';
import PageHeader from '../../../Common/PageHeader';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import FilterSidebar, { FilterValues, FilterConfig } from '../../../Common/FilterSidebar';

// Audit Log specific components
import AuditLogTable from './components/AuditLogTable';
import Pagination from '../../../pages/ShopServices/Orders/components/Pagination';
import './AuditLogs.css';

// Hooks
import { useDebounce } from '../../../hooks/useDebounce';

// API and types
import { getAuditLogs } from '../../../api/promos';
import { AuditLog, AuditLogListParams, AuditLogAction } from '../../../api/promos/types';
import { getPromos } from '../../../api/promos';
import { Promo } from '../../../api/promos/types';

const AuditLogsPage: React.FC = () => {
  const navigate = useNavigate();
  
  // State management
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  // Removed duplicate currentPage state - using filters.pageNum as single source of truth
  
  // Filters state
  const [filters, setFilters] = useState<AuditLogListParams>({
    pageNum: 1,
    pageSize: 20,
  });
  
  // Derived value for currentPage (using filters.pageNum as source of truth)
  const currentPage = filters.pageNum || 1;
  
  // Filter sidebar state
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  
  // Search input state (for immediate UI updates)
  const [searchInputValue, setSearchInputValue] = useState<string>(filters.promoName || '');
  
  // Debounce search input to prevent excessive API calls (300ms delay)
  const debouncedSearchTerm = useDebounce(searchInputValue, 300);
  
  // AbortController ref for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Promos list for filter dropdown
  const [promos, setPromos] = useState<Promo[]>([]);
  const [promosLoading, setPromosLoading] = useState(false);
  
  // Fetch promos for filter dropdown
  const fetchPromos = useCallback(async () => {
    try {
      setPromosLoading(true);
      const response = await getPromos({ pageSize: 100 });
      setPromos(response.promos || []);
    } catch (err) {
      console.error('Failed to fetch promos for filter:', err);
    } finally {
      setPromosLoading(false);
    }
  }, []);
  
  // Load promos on mount
  useEffect(() => {
    fetchPromos();
  }, [fetchPromos]);
  
  // Fetch audit logs data
  const fetchAuditLogs = useCallback(async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await getAuditLogs(filters);
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      setAuditLogs(response.results || []);
      setTotalCount(response.totalCount || 0);
      // Removed setCurrentPage - currentPage is now derived from filters.pageNum
    } catch (err: any) {
      // Don't set error if request was aborted (user changed filters or component unmounted)
      if (abortController.signal.aborted || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      setError(err.message || 'Failed to load audit logs. Please try again.');
    } finally {
      // Only update loading state if request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [filters]);
  
  // Load audit logs on mount and filter changes
  useEffect(() => {
    fetchAuditLogs();
    
    // Cleanup: cancel request if component unmounts or filters change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchAuditLogs]);
  
  // Sync filterValues with filters when filters change (but only if sidebar is closed to avoid conflicts)
  useEffect(() => {
    if (!showFilterSidebar) {
      setFilterValues({
        promoId: filters.promoId || undefined,
        action: filters.action || undefined,
        promoName: filters.promoName || undefined,
        startDate: filters.startAt || undefined,
        endDate: filters.endAt || undefined,
      });
    }
  }, [filters, showFilterSidebar]);
  
  // Sync searchInputValue with filters.promoName when filters change externally (e.g., from filter sidebar)
  useEffect(() => {
    if (filters.promoName !== searchInputValue && filters.promoName !== debouncedSearchTerm) {
      setSearchInputValue(filters.promoName || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.promoName]);
  
  // Trigger search when debounced value changes
  useEffect(() => {
    if (debouncedSearchTerm !== filters.promoName) {
      if (debouncedSearchTerm.trim() === '') {
        handleFilterChange({ promoName: undefined });
      } else {
        handleFilterChange({ promoName: debouncedSearchTerm });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);
  
  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Partial<AuditLogListParams>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      pageNum: 1, // Reset to first page when filters change
    }));
  }, []);
  
  // Handle search input change (immediate UI update, debounced API call)
  const handleSearchInputChange = useCallback((searchTerm: string) => {
    setSearchInputValue(searchTerm);
  }, []);
  
  // Handle search clear
  const handleSearchClear = useCallback(() => {
    setSearchInputValue('');
    handleFilterChange({ promoName: undefined });
  }, [handleFilterChange]);
  
  // Handle pagination
  const handlePageChange = useCallback((page: number) => {
    setFilters(prev => ({
      ...prev,
      pageNum: page,
    }));
  }, []);
  
  // Handle page size change
  const handlePageSizeChange = useCallback((size: number) => {
    setFilters(prev => ({
      ...prev,
      pageSize: size,
      pageNum: 1, // Reset to first page when page size changes
    }));
  }, []);
  
  // Handle filter sidebar
  const handleOpenFilterSidebar = () => {
    setShowFilterSidebar(true);
  };
  
  const handleCloseFilterSidebar = () => {
    setShowFilterSidebar(false);
  };
  
  const handleFilterChangeLocal = (key: string, value: string) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
  };
  
  const handleApplyFilters = (filters: FilterValues) => {
    // Validate date range before applying
    if (filters.startDate && filters.endDate && filters.endDate < filters.startDate) {
      // Don't apply filters if date range is invalid
      // The FilterSidebar component will show validation error
      return;
    }
    
    setFilterValues(filters);
    const newFilters: Partial<AuditLogListParams> = {};
    
    if (filters.promoId && filters.promoId !== 'all') {
      newFilters.promoId = filters.promoId;
    }
    if (filters.action && filters.action !== 'all') {
      newFilters.action = filters.action as AuditLogAction;
    }
    if (filters.promoName && filters.promoName.trim() !== '') {
      newFilters.promoName = filters.promoName.trim();
    }
    if (filters.startDate) {
      newFilters.startAt = filters.startDate;
    }
    if (filters.endDate) {
      newFilters.endAt = filters.endDate;
    }
    
    handleFilterChange(newFilters);
    handleCloseFilterSidebar();
  };
  
  const handleResetFilters = () => {
    setFilterValues({});
    handleFilterChange({
      promoId: undefined,
      action: undefined,
      promoName: undefined,
      startAt: undefined,
      endAt: undefined,
    });
    handleCloseFilterSidebar();
  };
  
  // Filter configuration
  const filterConfig: FilterConfig = {
    promoId: {
      label: 'Promo',
      options: [
        { value: 'all', label: 'All' },
        ...(promos && promos.length > 0 ? promos.map(promo => ({
          value: promo.id,
          label: promo.name || promo.code || promo.id,
        })) : []),
      ],
    },
    action: {
      label: 'Action',
      options: [
        { value: 'all', label: 'All' },
        { value: AuditLogAction.CREATED, label: 'Created' },
        { value: AuditLogAction.UPDATED, label: 'Updated' },
        { value: AuditLogAction.ACTIVATED, label: 'Activated' },
        { value: AuditLogAction.INACTIVATED, label: 'Inactivated' },
        { value: AuditLogAction.PAUSED, label: 'Paused' },
        { value: AuditLogAction.DELETED, label: 'Deleted' },
        { value: AuditLogAction.EXPIRED, label: 'Expired' },
      ],
    },
    dateRange: {
      label: 'Date Range',
      startDateLabel: 'Start Date',
      endDateLabel: 'End Date',
    },
  };
  
  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Promos', onClick: () => navigate('/promos') },
              { label: 'Audit Logs', active: true }
            ]} 
          />
        </div>
        
        {/* Page Header */}
        <div className="mb-4" style={{ padding: '0 15px' }}>
          <div>
            <h1 style={{ fontWeight: 600, fontSize: 24, marginBottom: 8, lineHeight: 1.2 }}>
              Audit Logs
            </h1>
            <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>
              Track all changes to promos and campaigns
            </p>
          </div>
        </div>
        
        {/* Error Alert */}
        {error && (
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </div>
        )}
        
        {/* Main Content */}
        <Row>
          <Col>
            <Card>
              <Card.Body style={{ padding: '24px' }}>
                {/* Search and Filter Bar */}
                <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: '16px' }}>
                  {/* Search Bar */}
                  <div className="flex-grow-1" style={{ maxWidth: '400px' }}>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by promo or user"
                        value={searchInputValue}
                        onChange={(e) => {
                          handleSearchInputChange(e.target.value);
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#E5E7EB';
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.outline = 'none';
                        }}
                        style={{
                          paddingLeft: '40px',
                          height: '40px',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          fontFamily: 'Helvetica Neue, Arial, sans-serif',
                          fontSize: '14px',
                        }}
                      />
                      <i 
                        className="ph-magnifying-glass" 
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#6B7280',
                          fontSize: '18px',
                        }}
                      />
                      {searchInputValue && (
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={handleSearchClear}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#6B7280',
                            padding: '4px',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          <i className="ph-x" style={{ fontSize: '16px' }}></i>
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Filter Button */}
                  <button
                    type="button"
                    onClick={handleOpenFilterSidebar}
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    style={{
                      height: '40px',
                      padding: '8px 16px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      color: '#374151',
                      fontFamily: 'Helvetica Neue, Arial, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
                    title="Filter"
                    aria-label="Filter"
                  >
                    <img
                      src="https://cdn-dev.sportsforlife.in/admin/static-assets/icons/general/filter-Icon.svg"
                      alt="Filter"
                      style={{ width: '20px', height: '20px' }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const icon = document.createElement('i');
                        icon.className = 'ph-funnel';
                        icon.style.fontSize = '18px';
                        icon.style.color = '#374151';
                        target.parentElement?.appendChild(icon);
                      }}
                    />
                    Filter
                  </button>
                </div>
                
                {/* Table */}
                {loading ? (
                  <ShimmerLoader type="table" rows={5} />
                ) : auditLogs.length > 0 ? (
                  <AuditLogTable
                    auditLogs={auditLogs}
                    totalCount={totalCount}
                    currentPage={currentPage}
                    pageSize={filters.pageSize || 20}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted mb-0">No audit logs found.</p>
                  </div>
                )}
              </Card.Body>
            </Card>
            
            {/* Pagination - Outside Card to match Products page structure */}
            {!loading && Math.ceil(totalCount / (filters.pageSize || 20)) > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / (filters.pageSize || 20))}
                totalCount={totalCount}
                pageSize={filters.pageSize || 20}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </Col>
        </Row>
        
        {/* Filter Sidebar */}
        <FilterSidebar
          show={showFilterSidebar}
          onClose={handleCloseFilterSidebar}
          filterConfig={filterConfig}
          filters={filterValues}
          onFilterChange={handleFilterChangeLocal}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />
      </Container>
    </div>
  );
};

export default AuditLogsPage;
